import React, { useEffect, useRef, useState } from 'react'
import { Field, FieldProps, FormikErrors, useFormikContext } from 'formik'
import { Currency } from '../../../models'
import { CurrencyCode, CurrencyLabel } from '../../../ui/currency'
import { OfferionErrorMessage } from '../../../forms/validation/OfferionErrorMessage'
import { useOfferionTranslation } from '../../../store/hooks/useOfferionTranslation'
import { numberAsText } from '../../../utils/formatters'
import {
    getQuickItemValidationMessages,
    isQuickItemDraftEmpty,
    parseQuickItemNumber,
    QuickItemDraft,
    quickItemDraftTotal,
    quickItemDraftUnitPrice,
    validateQuickItemDraft,
} from './quickDocumentItem'

interface QuickDocumentForm {
    quickItem: QuickItemDraft
}

interface Props {
    currency: Currency
    defaultVat: number
    isAccountingVat: boolean
    enterFullPriceEnabled: boolean
    nextIndex: number
    focusRequest: number
}

type DraftField = 'name' | 'quantity' | 'price' | 'discount'
type DraftFieldElement = HTMLInputElement | HTMLTextAreaElement

// With full price entry the price input sits in the total column, to the right of the discount,
// so tabbing has to follow the visual order of the cells.
const getDraftFieldOrder = (enterFullPriceEnabled: boolean): DraftField[] =>
    enterFullPriceEnabled ? ['name', 'quantity', 'discount', 'price'] : ['name', 'quantity', 'price', 'discount']

export const QuickDocumentItemRow = ({ currency, defaultVat, isAccountingVat, enterFullPriceEnabled, nextIndex, focusRequest }: Props) => {
    const { t } = useOfferionTranslation()
    const formik = useFormikContext<QuickDocumentForm>()
    const nameRef = useRef<HTMLTextAreaElement>(null)
    const quantityRef = useRef<HTMLInputElement>(null)
    const priceRef = useRef<HTMLInputElement>(null)
    const discountRef = useRef<HTMLInputElement>(null)
    const rowRef = useRef<HTMLLIElement>(null)
    const commitInProgress = useRef(false)
    const [focusedField, setFocusedField] = useState<DraftField | null>(null)

    useEffect(() => {
        if (focusRequest > 0) setFocusedField('name')
    }, [focusRequest])

    useEffect(() => {
        if (focusedField === 'name') nameRef.current?.focus()
        if (focusedField === 'quantity') quantityRef.current?.focus()
        if (focusedField === 'price') priceRef.current?.focus()
        if (focusedField === 'discount') discountRef.current?.focus()
    }, [focusedField])

    const draftFieldOrder = getDraftFieldOrder(enterFullPriceEnabled)
    const validationMessages = getQuickItemValidationMessages(t, enterFullPriceEnabled)
    const isReadyToSave = () => {
        return formik.values.quickItem.name.trim() !== '' && parseQuickItemNumber(formik.values.quickItem.price) != null
    }

    const commit = async () => {
        if (formik.isSubmitting || commitInProgress.current) return

        commitInProgress.current = true

        try {
            const touched = {
                name: true,
                quantity: true,
                price: true,
                discount: true,
            }

            await formik.setTouched({ ...formik.touched, quickItem: touched }, false)

            const errors = await formik.validateForm()
            const quickErrors = (errors.quickItem || {}) as FormikErrors<QuickItemDraft>
            const firstInvalidField = draftFieldOrder.find(field => quickErrors[field])

            if (firstInvalidField) {
                setFocusedField(firstInvalidField)
                return
            }

            await formik.submitForm()
        } finally {
            commitInProgress.current = false
        }
    }

    const onKeyDown = (event: React.KeyboardEvent<DraftFieldElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault()

            if (isReadyToSave()) void commit()

            return
        }

        // Safari keeps buttons out of the tab order by default, so the collapsed cells would
        // be skipped and focus would leave the row. Move between fields explicitly instead.
        if (event.key === 'Tab' && focusedField) {
            const nextField = draftFieldOrder[draftFieldOrder.indexOf(focusedField) + (event.shiftKey ? -1 : 1)]

            // Without a next field we let the event bubble, so focus leaves the row and commits.
            if (nextField) {
                event.preventDefault()
                setFocusedField(nextField)
            }
        }
    }

    const options = {
        index: nextIndex,
        defaultVat,
        isAccountingVat,
        enterFullPriceEnabled,
    }
    const draftErrors = validateQuickItemDraft(formik.values.quickItem, validationMessages)
    const isDraftCalculable = Object.keys(draftErrors).length === 0 && !isQuickItemDraftEmpty(formik.values.quickItem)
    const total = isDraftCalculable ? quickItemDraftTotal(formik.values.quickItem, options) : 0
    const unitPrice = isDraftCalculable && enterFullPriceEnabled ? quickItemDraftUnitPrice(formik.values.quickItem, options) : 0

    const renderDisplayValue = (field: DraftField, value: string, placeholder: string) => {
        if (field === 'name') {
            return value.trim() || <span className="table__quick-entry-display-placeholder">{placeholder}</span>
        }

        const parsedValue = parseQuickItemNumber(value)
        const displayedNumber = parsedValue == null ? value.trim() : numberAsText(parsedValue)

        if (field === 'price') {
            if (parsedValue != null) return <CurrencyLabel amount={parsedValue} currency={currency} />

            return (
                <>
                    <span className="table__quick-entry-display-placeholder">{displayedNumber || placeholder}</span>
                    <CurrencyCode currency={currency} />
                </>
            )
        }

        if (field === 'discount') {
            return `${displayedNumber}%`
        }

        return displayedNumber || <span className="table__quick-entry-display-placeholder">{placeholder}</span>
    }

    const renderInput = (
        field: DraftField,
        ref: React.RefObject<DraftFieldElement>,
        placeholder: string,
        inputMode?: 'decimal',
        multiline = false
    ) => (
        <Field name={`quickItem.${field}`}>
            {({ field: formikField, meta }: FieldProps<string>) => {
                const isFocused = focusedField === field
                const commonProps = {
                    ...formikField,
                    autoComplete: 'off',
                    'aria-label': placeholder,
                    disabled: formik.isSubmitting,
                    className: `table__quick-entry-input ${multiline ? 'table__quick-entry-input--name' : ''} ${
                        meta.touched && meta.error ? 'error' : ''
                    }`,
                    placeholder,
                    onKeyDown,
                    onFocus: () => {
                        setFocusedField(field)

                        if (field === 'discount' && parseQuickItemNumber(formikField.value) === 0) {
                            void formik.setFieldValue(formikField.name, '', false)
                        }
                    },
                    onBlur: (event: React.FocusEvent<DraftFieldElement>) => {
                        formikField.onBlur(event)

                        if (field === 'discount' && event.target.value.trim() === '') {
                            void formik.setFieldValue(formikField.name, '0', false)
                        }

                        setFocusedField(currentField => (currentField === field ? null : currentField))
                    },
                }

                return (
                    <>
                        {isFocused ? (
                            multiline ? (
                                <textarea {...commonProps} ref={ref as React.RefObject<HTMLTextAreaElement>} rows={1} />
                            ) : (
                                <input {...commonProps} ref={ref as React.RefObject<HTMLInputElement>} inputMode={inputMode} type="text" />
                            )
                        ) : (
                            <button
                                type="button"
                                className={`table__quick-entry-display ${
                                    field === 'name' ? 'table__quick-entry-display--name' : 'table__quick-entry-display--number'
                                }`}
                                disabled={formik.isSubmitting}
                                onClick={() => setFocusedField(field)}
                                onFocus={() => setFocusedField(field)}
                            >
                                {renderDisplayValue(field, formikField.value, placeholder)}
                            </button>
                        )}
                        <OfferionErrorMessage name={`quickItem.${field}`} showOnlyOnTouched={formik.submitCount === 0} />
                    </>
                )
            }}
        </Field>
    )

    return (
        <li
            ref={rowRef}
            className="table__row table__row--quick-entry"
            onBlur={() => {
                window.setTimeout(() => {
                    const row = rowRef.current

                    if (row && !row.contains(document.activeElement) && isReadyToSave()) void commit()
                }, 0)
            }}
        >
            <span className="table__cell table__cell--order table__item-inline-title table__item-inline-title--large-only table__quick-entry-order">
                +
            </span>
            <span className="table__cell table__cell--prefixed table__quick-entry-name" data-prefix={t('Invoice.Table.Header.name')}>
                {renderInput('name', nameRef, t('Invoice.Edit.itemNamePlaceholder'), undefined, true)}
            </span>
            <span
                className="table__cell table__cell__item-total table__cell__price table__cell--prefixed"
                data-prefix={t('Invoice.Table.Header.amount')}
            >
                {renderInput('quantity', quantityRef, t('Invoice.Edit.quantity'), 'decimal')}
            </span>
            <span
                className="table__cell table__cell__item-total table__cell__price table__cell--prefixed"
                data-prefix={t('Invoice.Table.Header.price')}
            >
                {enterFullPriceEnabled ? (
                    <span className="table__quick-entry-display table__quick-entry-display--number table__quick-entry-display--readonly">
                        <CurrencyLabel amount={unitPrice} currency={currency} />
                    </span>
                ) : (
                    renderInput('price', priceRef, t('Invoice.Edit.price'), 'decimal')
                )}
            </span>
            <span
                className="table__cell table__cell__item-total table__cell__price table__cell--prefixed"
                data-prefix={t('Invoice.Table.Header.discount')}
            >
                {renderInput('discount', discountRef, t('Invoice.Edit.discount'), 'decimal')}
            </span>
            <span
                className="table__cell table__cell__item-total table__cell__price table__cell--prefixed"
                data-prefix={t('Invoice.Table.Header.total')}
            >
                {enterFullPriceEnabled ? (
                    renderInput('price', priceRef, t('Invoice.Table.Header.total'), 'decimal')
                ) : (
                    <span className="table__quick-entry-display table__quick-entry-display--number table__quick-entry-display--readonly">
                        <CurrencyLabel amount={total} currency={currency} />
                    </span>
                )}
            </span>
            <span className="table__cell table__cell__item-total table__cell__price"></span>
        </li>
    )
}
